import {
  FILTER_OPTIONS,
  GAMEWEEK_WINDOW_PRESETS,
  SORT_OPTIONS
} from './useFixtureTickerFilters';
import type { FilterOption, SortOption } from './useFixtureTickerFilters';
import { MAX_GAMEWEEK } from './constants';
import type { TeamWithFixtures } from './constants';
import { TeamFilter } from './TeamFilter';
import SelectBox from '@components/selectbox';
import Toggle from '@components/toggle';
import GameweekRangeSlider from '@components/fpl-ui/gameweek-range-slider';
import { SliderPresetButton } from '@components/fpl-ui/range-slider-field';

type FixtureTickerFiltersProps = {
  selectedFilter: FilterOption;
  onFilterChange: (filter: FilterOption) => void;
  selectedSort: SortOption;
  onSortChange: (sort: SortOption) => void;
  teams: TeamWithFixtures[];
  selectedTeams: string[];
  onSelectedTeamsChange: (next: string[]) => void;
  gameweekStart: number;
  gameweekEnd: number;
  gameweekFloor: number;
  onGameweekRangeChange: (start: number, end: number) => void;
  onGameweekWindowChange: (windowSize: number) => void;
  showTeamStrength: boolean;
  onShowTeamStrengthChange: (next: boolean) => void;
};

export const FixtureTickerFilters = ({
  selectedFilter,
  onFilterChange,
  selectedSort,
  onSortChange,
  teams,
  selectedTeams,
  onSelectedTeamsChange,
  gameweekStart,
  gameweekEnd,
  gameweekFloor,
  onGameweekRangeChange,
  onGameweekWindowChange,
  showTeamStrength,
  onShowTeamStrengthChange
}: FixtureTickerFiltersProps) => {
  const filterOptions = FILTER_OPTIONS.map((option) => ({
    value: option.id,
    label: option.label
  }));

  const sortOptions = SORT_OPTIONS.map((option) => ({
    value: option.id,
    label: option.label
  }));

  const handleSortChange = (value: string) => {
    const next = SORT_OPTIONS.find((option) => option.id === value);
    if (next) onSortChange(next);
  };

  const selectedWindowSize = gameweekEnd - gameweekStart + 1;

  // Every control sits on one baseline: 14px labels with a 6px gap, 38px
  // controls, bottom-aligned. The selects and team filter have fixed widths
  // and the slider takes the remainder, with a floor that steps up with the
  // viewport. Below 2xl the card is too narrow for all five on one line, so
  // the row wraps and the toggle drops to a second line, right aligned; below
  // lg the controls stack.
  return (
    // relative + z-20 keeps the team dropdown above the table card, whose
    // pinned header cells carry their own stacking context.
    <div
      data-testid="fixture-ticker-filters"
      className="relative z-20 flex flex-col gap-4 rounded-[14px] border border-neutral-200 bg-white p-4 shadow-[0_1px_2px_rgba(16,24,40,0.04)] lg:flex-row lg:flex-wrap lg:items-end 2xl:flex-nowrap"
    >
      <div className="lg:w-[160px] lg:shrink-0">
        <SelectBox
          size="compact"
          name="fixture-ticker-filter"
          label="Sort by"
          value={selectedFilter}
          options={filterOptions}
          onChange={(value) => onFilterChange(value as FilterOption)}
        />
      </div>
      <div className="lg:w-[158px] lg:shrink-0">
        <SelectBox
          size="compact"
          name="fixture-ticker-sort-direction"
          label="Order"
          value={selectedSort.id}
          options={sortOptions}
          onChange={handleSortChange}
        />
      </div>
      <div className="lg:w-[236px] lg:shrink-0">
        <TeamFilter
          teams={teams}
          selectedTeams={selectedTeams}
          onChange={onSelectedTeamsChange}
        />
      </div>
      <div className="min-w-0 flex-1 lg:min-w-[280px] 2xl:min-w-[380px]">
        <GameweekRangeSlider
          name="fixture-ticker-gw-range"
          min={gameweekFloor}
          max={MAX_GAMEWEEK}
          value={[gameweekStart, gameweekEnd]}
          onChange={([start, end]) => onGameweekRangeChange(start, end)}
          actions={
            <>
              <span className="font-open-sans text-xs text-neutral-500">
                quick set
              </span>
              {GAMEWEEK_WINDOW_PRESETS.map((windowSize) => (
                <SliderPresetButton
                  key={windowSize}
                  testId={`gameweek-window-${windowSize}`}
                  isActive={selectedWindowSize === windowSize}
                  onClick={() => onGameweekWindowChange(windowSize)}
                >
                  {windowSize}
                </SliderPresetButton>
              ))}
            </>
          }
        />
      </div>
      <div className="lg:ml-auto lg:shrink-0 lg:self-end">
        <Toggle
          name="fixture-ticker-show-strength"
          checked={showTeamStrength}
          onChange={() => onShowTeamStrengthChange(!showTeamStrength)}
          text="Team strength"
        />
      </div>
    </div>
  );
};
